import { buildSync } from "@/api/endpoints/build";
import { createSearch, getOngoingSearches } from "@/api/endpoints/searches";
import { getOrganization } from "@/api/endpoints/organization";
import { SEARCH_TYPES } from "@/constants";
import { padUnsavedQuery } from "@/containers/QueryBuilderNew/requestUtils";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import { ROUTES } from "@/routes_new";
import { formatDateForApi } from "@/utils";
import AllEarsTitle from "@components/AllEarsTitle";
import SignupImage from "@components/SignupImage";
import { Button } from "@components/ui/button";
import ErrorComponent from "@components/ui/error";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@components/ui/item";
import Logo from "@components/ui/logo";
import { Spinner } from "@components/ui/spinner";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import {
  BarChart3Icon,
  RocketIcon,
  SquarePenIcon,
  UserPlusIcon,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { generatePath } from "react-router-dom";
import { toast } from "sonner";

export default function WelcomeNew() {
  const { t } = useLingui();

  // Local storage state
  const [companyName] = useLocalStorage<string | undefined>(
    "onboarding/company-name",
    undefined
  );
  const [fullCompanyName] = useLocalStorage<string | undefined>(
    "onboarding/full-company-name",
    undefined
  );
  const [companyDescription] = useLocalStorage<string | undefined>(
    "onboarding/company-description",
    undefined
  );

  // Server state
  const { data: organizationData, error: organizationError } = useQuery({
    queryKey: ["organizations"],
    queryFn: getOrganization,
  });

  const { data: existingSearches } = useQuery({
    queryKey: ["ongoing-searches"],
    queryFn: getOngoingSearches,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.ok && data.data.length > 0) return false;
      return 3000;
    },
  });

  // Helpers
  const firstOrganization = useMemo(() => {
    return organizationData?.ok ? organizationData.data[0] : undefined;
  }, [organizationData]);

  const existingSearch = useMemo(() => {
    if (!existingSearches?.ok) return undefined;
    return existingSearches.data[0];
  }, [existingSearches]);

  // Mutations
  const {
    mutate: mutateCreateSearch,
    isPending: isCreateSearchPending,
    data: createSearchData,
  } = useMutation({
    mutationFn: createSearch,
    onSuccess: (result) => {
      if (!result.ok || !firstOrganization) {
        toast.error(t`Kunde inte skapa din första bevakning`);
      }
    },
    onError: () => {
      toast.error(t`Kunde inte skapa din första bevakning`);
    },
  });

  const { mutate: mutateBuildSync, isPending: isBuildSyncPending } =
    useMutation({
      mutationFn: buildSync,
      onSettled: (buildResult) => {
        if (!firstOrganization) return;

        const sq = buildResult?.query_structure.search_query;
        const fallbackKeywords = [companyName, fullCompanyName].filter(Boolean);

        mutateCreateSearch({
          title: companyName,
          start_date: formatDateForApi(subDays(new Date(), 7)),
          search_type: SEARCH_TYPES.BRAND,
          information_need:
            buildResult?.query_structure.disambiguation.question,
          queries: [
            padUnsavedQuery({
              keywords: sq?.keywords?.length ? sq.keywords : fallbackKeywords,
              near_keywords: sq?.near_keywords,
              not_near_keywords: sq?.not_near_keywords,
              near_distance: sq?.near_distance,
              not_near_distance: sq?.not_near_distance,
              languages: sq?.languages?.length
                ? sq.languages
                : firstOrganization.languages,
            }),
          ],
        });
      },
    });

  const isCreating = isBuildSyncPending || isCreateSearchPending;

  // Fallback: only build from frontend if no search was created by backend
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (hasStartedRef.current || !firstOrganization || !companyName) return;
    if (existingSearch) return;
    if (existingSearches === undefined) return;
    hasStartedRef.current = true;

    mutateBuildSync({
      company_name: companyName,
      full_company_name: fullCompanyName,
      company_description: companyDescription,
      language: firstOrganization.languages[0],
      max_mentions: 20,
    });
  }, [
    firstOrganization,
    companyName,
    fullCompanyName,
    companyDescription,
    existingSearch,
    existingSearches,
    mutateBuildSync,
  ]);

  const isReady = !!existingSearch || (createSearchData?.ok && !isCreating);

  // Redirect after CTA click once a search is available
  const [hasClickedCta, setHasClickedCta] = useState(false);
  useEffect(() => {
    if (!hasClickedCta || !firstOrganization) return;

    const uuid =
      existingSearch?.uuid ??
      (createSearchData?.ok ? createSearchData.data.uuid : undefined);
    if (!uuid) return;

    window.location.href = generatePath(ROUTES.MENTION_DETAIL_ORGID, {
      orgUuid: firstOrganization.uuid,
      uuid,
    });
  }, [hasClickedCta, existingSearch, createSearchData, firstOrganization]);

  // Handlers
  const handleCtaClick = () => {
    setHasClickedCta(true);
  };

  return (
    <>
      <AllEarsTitle title={t`Aktivera ditt konto`} />

      <div className="md:h-svh flex">
        <div className="p-7 md:p-14 flex flex-col gap-[20svh] overflow-y-auto flex-1">
          <Logo width={80} className="self-start" />

          <div className="max-w-xl mx-auto">
            <AnimatePresence mode="popLayout">
              {firstOrganization ? (
                <motion.div
                  key="welcome"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-8"
                >
                  <div className="space-y-1.5 max-w-sm text-balance">
                    <h2 className="text-2xl font-semibold">
                      <Trans>Nu är du igång 👋</Trans>
                    </h2>
                    <p className="text-muted-foreground text-sm/relaxed">
                      <Trans>
                        Din första bevakning är redan live – gå till flödet och
                        se vad vi har hittat. Vill du ta det ett steg till?
                      </Trans>
                    </p>
                  </div>

                  <div className="space-y-6">
                    <Item className="p-0">
                      <ItemMedia variant="icon">
                        <UserPlusIcon />
                      </ItemMedia>
                      <ItemContent>
                        <ItemTitle>
                          <Trans>Bjud in ditt team</Trans>
                        </ItemTitle>
                        <ItemDescription>
                          <Trans>Insikter blir bättre när de delas</Trans>
                        </ItemDescription>
                      </ItemContent>
                    </Item>

                    <Item className="p-0">
                      <ItemMedia variant="icon">
                        <SquarePenIcon />
                      </ItemMedia>
                      <ItemContent>
                        <ItemTitle>
                          <Trans>Lägg till en bevakning</Trans>
                        </ItemTitle>
                        <ItemDescription>
                          <Trans>
                            Följ en konkurrent, produkt eller ett ämne
                          </Trans>
                        </ItemDescription>
                      </ItemContent>
                    </Item>

                    <Item className="p-0">
                      <ItemMedia variant="icon">
                        <BarChart3Icon />
                      </ItemMedia>
                      <ItemContent>
                        <ItemTitle>
                          <Trans>Utforska analys & rapporter</Trans>
                        </ItemTitle>
                        <ItemDescription>
                          <Trans>
                            Se trender, räckvidd och sentiment över tid
                          </Trans>
                        </ItemDescription>
                      </ItemContent>
                    </Item>
                  </div>

                  <Button
                    className="w-full"
                    disabled={hasClickedCta && !isReady}
                    onClick={handleCtaClick}
                  >
                    <Trans>Utforska omnämnanden</Trans>
                    {hasClickedCta && !isReady ? <Spinner /> : <RocketIcon />}
                  </Button>
                </motion.div>
              ) : organizationError ? (
                <motion.div
                  key="error"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <ErrorComponent
                    description={t` Hmm... Någonting gick snett på vårt håll. Prova att ladda om sidan igen. Om det fortfarande strular, hör gärna av dig till oss! `}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="loading"
                  className="flex flex-col items-center"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <p className="text-center text-muted-foreground">
                    <Trans>Laddar...</Trans>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <SignupImage />
      </div>
    </>
  );
}
